import { defineStore } from 'pinia';
import axios from 'axios';
import api from '../api/axios';
import permissionService from '../services/permissionService';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: (() => {
      try {
        const userData = localStorage.getItem('userData');
        return userData ? JSON.parse(userData) : null;
      } catch (error) {
        console.warn('Failed to parse user data from localStorage:', error);
        // Clean up corrupted data
        localStorage.removeItem('userData');
        return null;
      }
    })(),
    isAuthenticatedValue: false,
    isLoadingValue: true, // Start with loading true to prevent flash of unauthenticated content
    currentBusiness: null,
    permissionsInitialized: false
  }),

  actions: {
    setUser(user) {
      this.user = user
      if (user) {
        this.isAuthenticatedValue = true
        localStorage.setItem('userData', JSON.stringify(user))
        
        // Initialize permissions after setting user
        this.initializePermissions()
      }
      this.isLoadingValue = false;
    },

    async initializeAuth() {
      await this.checkAuthStatus();
    },

    async checkAuthStatus() {
      this.isLoadingValue = true;
      
      try {
        // Make a request to verify if the httpOnly cookie is valid
        const response = await axios.get('/api/auth/auth-status', {
          withCredentials: true
        });

        if (response.status === 200) {
          // Auth is valid, check if we have user data in localStorage
          const savedUserData = localStorage.getItem('userData');

          if (savedUserData) {
            try {
              const userData = JSON.parse(savedUserData);
              // Check if userData is valid (not null/undefined and has required properties)
              if (userData && userData.firstName) {
                this.user = userData;
                this.isAuthenticatedValue = true;
                this.isLoadingValue = false;
                
                // Initialize permissions for existing user
                const permissionsLoaded = await this.initializePermissions();
                if (!permissionsLoaded) {                  
                  await this.logout();
                }
                return;
              }
            } catch (error) {
              console.warn('Failed to parse saved user data:', error);
            }
          }
        }

        this.isAuthenticatedValue = false;
        this.isLoadingValue = false;
        this.clearUser();

      } catch (error) {
        this.isAuthenticatedValue = false;
        this.isLoadingValue = false;
        this.clearUser();
      }
    },

    async initializePermissions() {
      // Don't reload permissions if they're already initialized for this user
      if (this.permissionsInitialized && permissionService.initialized) {
        return true;
      }

      if (!this.user) {
        console.warn('Cannot initialize permissions: user not loaded');
        this.permissionsInitialized = false;
        return false;
      }

      try {
        // Call your backend function to get permissions
        const response = await axios.get('/api/auth/permissions', {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });

        if (response.status === 200) {
          const data = response.data;
          if (data?.code === 'ok') {
            const permissions = data.data.permissions || [];
            
            // Check if permissions array is empty or invalid
            if (!Array.isArray(permissions) || permissions.length === 0) {
              console.error('No permissions returned for user - invalid state');
              this.permissionsInitialized = false;
              return false;
            }
            
            // Initialize the permission service with the data
            permissionService.userPermissions = new Set(permissions);
            permissionService.userRole = data.data.role;
            permissionService.initialized = true;
            this.permissionsInitialized = true;
            
            return true;
          } else {
            console.error('Permissions API returned error:', data?.message || 'Unknown error');
            this.permissionsInitialized = false;
            return false;
          }
        }
        
        console.error('Failed to load permissions - HTTP error:', response.status);
        this.permissionsInitialized = false;
        return false;
        
      } catch (error) {
        console.error('Error initializing permissions:', error);
        this.permissionsInitialized = false;
        return false;
      }
    },

    async logout() {
      try {
        // Shared instance so the CSRF header interceptor applies (logout rejects without it)
        await api.post('/api/auth/logout', {}, { skipAuthRefresh: true });
      } catch (error) {
        console.error('Logout error:', error);
      }
      this.clearUser()
    },

    clearUser() {
      this.user = null
      this.isAuthenticatedValue = false
      this.isLoadingValue = false
      this.permissionsInitialized = false
      localStorage.removeItem('userData')
      
      // Clear permissions from service
      permissionService.clear()
    },
  },

  getters: {
    isAuthenticated: (state) => state.isAuthenticatedValue,
    getPreferredName: (state) => state.user?.firstName ?? '',
    isLoading: (state) => state.isLoadingValue,
    isPermissionsReady: (state) => state.permissionsInitialized && permissionService.initialized,
    
    hasPermission: () => (permission) => {
      return permissionService.hasPermission(permission)
    },
    hasRole: () => (role) => {
      return permissionService.hasRole(role)
    },
    canAccessRoute: () => (routeName) => {
      return permissionService.canAccessRoute(routeName)
    },
    canAccessSection: () => (sectionId) => {
      return permissionService.canAccessSection(sectionId)
    },
    canAccessSubSection: () => (parentSection, subSectionId) => {
      return permissionService.canAccessSubSection(parentSection, subSectionId)
    }
  }
})